import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { extractPropertyMetadata } from '../utils/firestoreQueries';

/**
 * Viewings Service - Complete bezichtigingen functionaliteit
 * Volgt de StudyStay data model specificaties
 */

// Types (JSDoc voor TypeScript-like intellisense)
/**
 * @typedef {Object} Viewing
 * @property {string} id - Firestore doc id
 * @property {string} listingId - Verwijst naar listings/{id}
 * @property {string} landlordUid - Eigenaar listing
 * @property {string} tenantUid - Student die plant of uitnodiging krijgt
 * @property {'tenant'|'landlord'} requestedBy - Wie heeft de bezichtiging aangevraagd
 * @property {Timestamp} startAt - Geplande start
 * @property {Timestamp} endAt - Geplande einde
 * @property {string} location - Standaard: listing.address
 * @property {'pending'|'confirmed'|'declined'|'cancelled'|'completed'|'no_show'} status
 * @property {string} [notes] - Optioneel, bv. verzamelpunt
 * @property {Timestamp} createdAt
 * @property {Timestamp} updatedAt
 */

/**
 * @typedef {Object} LandlordAvailability
 * @property {string} id - Firestore doc id
 * @property {string} landlordUid
 * @property {number} weekday - 0=zo ... 6=za
 * @property {string} startTime - "09:00"
 * @property {string} endTime - "17:00"
 * @property {number} durationMin - bijv. 20
 * @property {number} [breakMin] - bijv. 10 tussen slots
 * @property {string} timezone - "Europe/Brussels"
 * @property {Timestamp} createdAt
 * @property {Timestamp} updatedAt
 */

/**
 * @typedef {Object} ViewingSlot
 * @property {Date} startAt
 * @property {Date} endAt
 * @property {boolean} available
 * @property {string} [reason] - Waarom niet beschikbaar
 */

// ===== VIEWINGS SERVICE =====

/**
 * Maak een nieuwe bezichtiging aan
 * @param {Object} viewingData - Viewing data zonder id, createdAt, updatedAt
 * @returns {Promise<string>} - Viewing ID
 */
export const createViewing = async (viewingData) => {
  try {
    // Validatie
    if (!viewingData.listingId || !viewingData.landlordUid || !viewingData.tenantUid) {
      throw new Error('Missing required fields: listingId, landlordUid, tenantUid');
    }

    if (!viewingData.startAt || !viewingData.endAt) {
      throw new Error('Missing required fields: startAt, endAt');
    }

    // Enforce max 1 active viewing per tenant per property (check both collections)
    const existingViewingsQuery = query(
      collection(db, 'viewings'),
      where('tenantUid', '==', viewingData.tenantUid),
      where('listingId', '==', viewingData.listingId),
      where('status', 'in', ['pending', 'confirmed'])
    );
    const existingSnap = await getDocs(existingViewingsQuery);
    if (!existingSnap.empty) {
      throw new Error('Je hebt al een actieve bezichtigingsaanvraag voor dit pand');
    }

    // Also check viewingRequests collection (iOS app)
    const propertyId = viewingData.listingId || viewingData.propertyId;
    if (propertyId) {
      const vrQuery = query(
        collection(db, 'viewingRequests'),
        where('tenantID', '==', viewingData.tenantUid),
        where('kotID', '==', propertyId)
      );
      const vrSnap = await getDocs(vrQuery).catch(() => ({ docs: [] }));
      const hasActiveVR = vrSnap.docs.some(d => {
        const s = d.data().status || '';
        return s === 'pending' || s === 'accepted' || s === 'confirmed';
      });
      if (hasActiveVR) {
        throw new Error('Je hebt al een actieve bezichtigingsaanvraag voor dit pand');
      }
    }

    // Check voor overlapping viewings
    const hasOverlap = await checkViewingOverlap(
      viewingData.landlordUid, 
      viewingData.startAt, 
      viewingData.endAt
    );

    if (hasOverlap) {
      throw new Error('Er is al een bezichtiging gepland op dit tijdstip');
    }

    // Check minimale lead time (2 uur)
    const now = new Date();
    const startTime = viewingData.startAt.toDate ? viewingData.startAt.toDate() : new Date(viewingData.startAt);
    const minLeadTime = 2 * 60 * 60 * 1000; // 2 uur in ms

    if (startTime.getTime() - now.getTime() < minLeadTime) {
      throw new Error('Bezichtigingen moeten minimaal 2 uur van tevoren worden aangevraagd');
    }

    // Fetch property to get denormalized metadata
    let propertyMetadata = {
      agencyId: null,
      clientLinkId: null,
      ownerId: null,
      propertyId: viewingData.listingId || viewingData.propertyId || null
    };

    if (propertyMetadata.propertyId) {
      try {
        const propertyDoc = await getDoc(doc(db, 'koten', propertyMetadata.propertyId));
        if (propertyDoc.exists()) {
          const propertyData = { id: propertyDoc.id, ...propertyDoc.data() };
          propertyMetadata = extractPropertyMetadata(propertyData);
        }
      } catch (error) {
        console.warn('Could not fetch property metadata for viewing:', error);
        // Continue without metadata - viewing will still be created
      }
    }

    // Viewings are allowed for all subscription plans.

    const viewing = {
      ...viewingData,
      status: 'pending',
      location: viewingData.location || '',
      notes: viewingData.notes || '',
      coTenants: viewingData.coTenants || [], // Array van user IDs van mede-huurders
      // Denormalized fields for optimized queries
      agencyId: propertyMetadata.agencyId,
      clientLinkId: propertyMetadata.clientLinkId,
      ownerId: propertyMetadata.ownerId || viewingData.landlordUid,
      propertyId: propertyMetadata.propertyId || viewingData.listingId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'viewings'), viewing);

    // Mirror to legacy viewingRequests collection for iOS compatibility (tenant requests only)
    if (viewingData.requestedBy === 'tenant') {
      try {
        const legacyRequest = {
          // iOS-compatible identifiers
          kotID: viewing.propertyId || viewing.listingId,
          listingId: viewing.listingId || viewing.propertyId,
          propertyId: viewing.propertyId || viewing.listingId,
          agencyId: viewing.agencyId || null,
          clientLinkId: viewing.clientLinkId || null,
          ownerId: viewing.ownerId || null,
          // Actor fields (multiple variants for compatibility)
          landlordID: viewing.landlordUid || viewing.landlordId || null,
          landlordId: viewing.landlordUid || viewing.landlordId || null,
          landlordUid: viewing.landlordUid || viewing.landlordId || null,
          tenantID: viewing.tenantUid || null,
          tenantId: viewing.tenantUid || null,
          tenantUid: viewing.tenantUid || null,
          // Timing
          start: viewing.startAt,
          end: viewing.endAt,
          startAt: viewing.startAt,
          endAt: viewing.endAt,
          // Status and context
          status: viewing.status || 'pending',
          location: viewing.location || '',
          notes: viewing.notes || '',
          requestedBy: 'tenant',
          source: 'web',
          viewingsId: docRef.id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await addDoc(collection(db, 'viewingRequests'), legacyRequest);
      } catch (error) {
        console.warn('Could not mirror viewing request to viewingRequests:', error);
      }
    }

    return docRef.id;
  } catch (error) {
    console.error('Error creating viewing:', error);
    throw error;
  }
};

/**
 * Update viewing status
 * Werkt met zowel 'viewings' (web app) als 'viewingRequests' (iOS app) collecties
 * @param {string} viewingId 
 * @param {'pending'|'confirmed'|'declined'|'cancelled'|'completed'|'no_show'|'accepted'} status
 * @param {string} [reason] - Optionele reden
 */
export const updateViewingStatus = async (viewingId, status, reason = '', currentUserId = null) => {
  try {
    console.log('updateViewingStatus called:', { viewingId, status, reason, currentUserId });
    
    if (!viewingId) {
      throw new Error('Geen bezichtiging ID opgegeven');
    }

    // Probeer eerst in 'viewings' collectie
    let viewingRef = doc(db, 'viewings', viewingId);
    let viewingDoc = await getDoc(viewingRef);
    let isViewingRequest = false;

    // Als het niet in 'viewings' zit, probeer 'viewingRequests' (iOS app)
    if (!viewingDoc.exists()) {
      console.log('Viewing not found in viewings collection, trying viewingRequests...');
      viewingRef = doc(db, 'viewingRequests', viewingId);
      viewingDoc = await getDoc(viewingRef);
      isViewingRequest = true;
      
      if (!viewingDoc.exists()) {
        throw new Error('Bezichtiging niet gevonden in viewings of viewingRequests');
      }
    }

    const viewingData = viewingDoc.data();
    console.log('=== VIEWING DATA DEBUG ===');
    console.log('Viewing ID:', viewingId);
    console.log('Is viewingRequest (iOS app):', isViewingRequest);
    console.log('Current user UID:', currentUserId);
    
    // Normaliseer veldnamen - iOS app gebruikt andere namen
    const landlordUid = viewingData.landlordUid || viewingData.landlordId || viewingData.landlordID || null;
    const tenantUid = viewingData.tenantUid || viewingData.tenantId || viewingData.tenantID || null;
    const startAt = viewingData.startAt || viewingData.start; // iOS gebruikt 'start'
    const endAt = viewingData.endAt || viewingData.end; // iOS gebruikt 'end'
    
    console.log('Viewing data:', JSON.stringify({
      landlordUid,
      tenantUid,
      status: viewingData.status,
      startAt: startAt,
      endAt: endAt,
      kotID: viewingData.kotID,
      listingId: viewingData.listingId
    }, null, 2));

    // Verify user has permission (client-side check for better error messages)
    if (currentUserId) {
      const isLandlord = landlordUid === currentUserId;
      const isTenant = tenantUid === currentUserId;
      const explicitAgencyId = viewingData.agencyId || viewingData.management?.agencyId || null;
      let isManagingAgency = explicitAgencyId === currentUserId;

      if (!isManagingAgency) {
        const candidatePropertyId = viewingData.propertyId || viewingData.listingId || viewingData.kotID || viewingData.kotId || null;
        if (candidatePropertyId) {
          try {
            const propertyDoc = await getDoc(doc(db, 'koten', candidatePropertyId));
            if (propertyDoc.exists()) {
              const propertyData = propertyDoc.data() || {};
              isManagingAgency = propertyData.management?.agencyId === currentUserId;
            }
          } catch (lookupError) {
            console.warn('Could not validate managing agency for viewing update:', lookupError);
          }
        }
      }
      
      console.log('=== PERMISSION CHECK ===');
      console.log('Current user UID:', currentUserId);
      console.log('Landlord UID (from viewing):', landlordUid);
      console.log('Tenant UID (from viewing):', tenantUid);
      console.log('Is landlord?', isLandlord);
      console.log('Is tenant?', isTenant);
      console.log('Is managing agency?', isManagingAgency);
      console.log('Has permission?', isLandlord || isTenant || isManagingAgency);
      
      if (!isLandlord && !isTenant && !isManagingAgency) {
        const errorMsg = `Geen toestemming. Je UID (${currentUserId}) komt niet overeen met landlordUid (${landlordUid}) of tenantUid (${tenantUid}) en is ook geen gekoppelde agency.`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
    } else {
      console.warn('No currentUserId provided, skipping client-side permission check');
    }

    console.log('Updating viewing document:', viewingId, 'in collection:', isViewingRequest ? 'viewingRequests' : 'viewings');
    
    // Voor viewingRequests (iOS app): map status naar iOS format (accepted -> confirmed)
    let updateStatus = status;
    if (isViewingRequest && status === 'confirmed') {
      updateStatus = 'accepted'; // iOS app gebruikt 'accepted' in plaats van 'confirmed'
    }
    
    try {
      const updateData = {
        status: updateStatus,
        reason: reason || '',
        updatedAt: serverTimestamp()
      };
      
      // Voor viewingRequests, voeg ook decisionAt toe
      if (isViewingRequest && (status === 'confirmed' || status === 'accepted' || status === 'declined')) {
        updateData.decisionAt = serverTimestamp();
      }
      
      await updateDoc(viewingRef, updateData);

      // Keep mirrored web/iOS documents in sync.
      // When landlord updates a viewingRequest, also update its linked viewings doc.
      if (isViewingRequest && viewingData.viewingsId) {
        try {
          const linkedViewingsRef = doc(db, 'viewings', viewingData.viewingsId);
          const linkedStatus = status === 'accepted' ? 'confirmed' : status;
          await updateDoc(linkedViewingsRef, {
            status: linkedStatus,
            reason: reason || '',
            updatedAt: serverTimestamp()
          });
        } catch (mirrorError) {
          console.warn('Could not sync linked viewings document:', mirrorError);
        }
      }
    } catch (updateError) {
      console.error('Firestore update error:', updateError);
      // Als het een permission error is, geef een duidelijkere melding
      if (updateError.code === 'permission-denied') {
        throw new Error('Geen toestemming om deze bezichtiging te wijzigen. De Firestore security rules blokkeren deze actie. Check of de rules zijn gedeployed en of je UID overeenkomt met landlordUid of tenantUid.');
      }
      throw updateError;
    }
    
    console.log('Viewing status updated successfully:', viewingId, status);
    
    // Als viewing wordt bevestigd, voer automatische cleanup uit
    // Normaliseer viewingData voor cleanup functie
    const normalizedViewingData = {
      ...viewingData,
      landlordUid: landlordUid || viewingData.landlordUid || viewingData.landlordId || viewingData.landlordID,
      tenantUid: tenantUid || viewingData.tenantUid || viewingData.tenantId || viewingData.tenantID,
      startAt: startAt || viewingData.startAt,
      endAt: endAt || viewingData.endAt
    };
    
    if (status === 'confirmed' || (isViewingRequest && status === 'accepted')) {
      console.log('Viewing confirmed/accepted, starting cleanup process...');
      await cleanupOtherViewings(normalizedViewingData, viewingId, currentUserId, isViewingRequest);
    }
    
    // Real-time subscriptions zorgen automatisch voor UI updates
  } catch (error) {
    console.error('Error updating viewing status:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      viewingId,
      status
    });
    throw error;
  }
};

/**
 * Cleanup andere viewings wanneer een viewing wordt bevestigd:
 * 1. Verwijder/afwijs alle andere pending viewings van dezelfde tenant
 * 2. Afwijs alle andere pending viewings voor hetzelfde tijdslot van andere tenants
 * Werkt met zowel 'viewings' als 'viewingRequests' collecties
 */
const cleanupOtherViewings = async (confirmedViewingData, confirmedViewingId, currentUserId, isViewingRequest = false) => {
  try {
    console.log('=== CLEANUP OTHER VIEWINGS ===');
    
    // Normaliseer landlordUid - iOS app gebruikt landlordID
    const landlordUid = confirmedViewingData.landlordUid || confirmedViewingData.landlordId || confirmedViewingData.landlordID;
    const tenantUid = confirmedViewingData.tenantUid || confirmedViewingData.tenantId || confirmedViewingData.tenantID;
    
    if (!landlordUid) {
      console.warn('No landlordUid found, skipping cleanup');
      return;
    }

    // Parse tijdslot
    const startAt = confirmedViewingData.startAt?.toDate ? confirmedViewingData.startAt.toDate() : new Date(confirmedViewingData.startAt);
    const endAt = confirmedViewingData.endAt?.toDate ? confirmedViewingData.endAt.toDate() : new Date(confirmedViewingData.endAt);

    console.log('Cleaning up viewings for landlord:', landlordUid);
    console.log('Confirmed viewing time slot:', startAt, 'to', endAt);
    console.log('Confirmed viewing tenant:', tenantUid);

    // Haal alle pending viewings op voor deze landlord
    // Check zowel 'viewings' als 'viewingRequests' collecties
    const allPendingViewings = [];
    
    // Query 'viewings' collectie (web app)
    try {
      let viewingsQuery = query(
        collection(db, 'viewings'),
        where('landlordUid', '==', landlordUid)
      );
      const viewingsSnapshot = await getDocs(viewingsQuery);
      const viewings = viewingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        _collection: 'viewings'
      }));
      allPendingViewings.push(...viewings);
    } catch (error) {
      console.warn('Error querying viewings collection:', error);
    }
    
    // Query 'viewingRequests' collectie (iOS app)
    try {
      let viewingRequestsQuery = query(
        collection(db, 'viewingRequests'),
        where('landlordID', '==', landlordUid)
      );
      const viewingRequestsSnapshot = await getDocs(viewingRequestsQuery);
      const viewingRequests = viewingRequestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        _collection: 'viewingRequests'
      }));
      allPendingViewings.push(...viewingRequests);
    } catch (error) {
      console.warn('Error querying viewingRequests collection:', error);
    }
    
    // Filter op pending status
    const pendingViewings = allPendingViewings.filter(v => v.status === 'pending');

    console.log('Found', pendingViewings.length, 'pending viewings');

    const batch = [];
    let declinedCount = 0;
    let cancelledCount = 0;

    for (const viewing of pendingViewings) {
      // Skip de viewing die we net hebben bevestigd
      if (viewing.id === confirmedViewingId) {
        continue;
      }

      // Skip mirrored counterpart records (same logical request across collections).
      const confirmedLinkedViewingsId = confirmedViewingData.viewingsId || null;
      if (confirmedLinkedViewingsId && viewing.id === confirmedLinkedViewingsId) {
        continue;
      }
      if (!confirmedLinkedViewingsId && viewing._collection === 'viewingRequests' && viewing.viewingsId === confirmedViewingId) {
        continue;
      }

      // Normaliseer veldnamen voor viewingRequests (iOS app)
      const viewingStartAtRaw = viewing.startAt || viewing.start;
      const viewingEndAtRaw = viewing.endAt || viewing.end;
      const viewingStartAt = viewingStartAtRaw?.toDate ? viewingStartAtRaw.toDate() : new Date(viewingStartAtRaw);
      const viewingEndAt = viewingEndAtRaw?.toDate ? viewingEndAtRaw.toDate() : new Date(viewingEndAtRaw);
      const viewingTenantUid = viewing.tenantUid || viewing.tenantId || viewing.tenantID;
      const viewingCollection = viewing._collection || (viewing.kotID ? 'viewingRequests' : 'viewings');

      let shouldUpdate = false;
      let newStatus = null;
      let reason = '';

      // Check 1: Zelfde tenant - verwijder alle andere aanvragen van deze tenant
      if (viewingTenantUid === tenantUid) {
        console.log('Cancelling other viewing from same tenant:', viewing.id);
        shouldUpdate = true;
        newStatus = viewingCollection === 'viewingRequests' ? 'cancelled' : 'cancelled';
        reason = 'Andere bezichtiging is bevestigd';
        cancelledCount++;
      }
      // Check 2: Zelfde tijdslot maar andere tenant - afwijs
      else if (hasTimeOverlap(startAt, endAt, viewingStartAt, viewingEndAt)) {
        console.log('Declining overlapping viewing from different tenant:', viewing.id);
        console.log('Overlap details:', {
          confirmedSlot: { start: startAt, end: endAt },
          viewingSlot: { start: viewingStartAt, end: viewingEndAt }
        });
        shouldUpdate = true;
        newStatus = 'declined';
        reason = 'Tijdslot is al bezet';
        declinedCount++;
      }

      if (shouldUpdate && newStatus) {
        const viewingRef = doc(db, viewingCollection, viewing.id);
        const updateData = {
          status: newStatus,
          reason: reason,
          updatedAt: serverTimestamp()
        };
        
        // Voor viewingRequests, voeg decisionAt toe
        if (viewingCollection === 'viewingRequests') {
          updateData.decisionAt = serverTimestamp();
        }
        
        batch.push(updateDoc(viewingRef, updateData));
      }
    }

    console.log(`Updating ${batch.length} viewings: ${cancelledCount} cancelled, ${declinedCount} declined`);

    // Execute alle updates in parallel
    if (batch.length > 0) {
      await Promise.all(batch);
      console.log('Cleanup completed successfully');
    } else {
      console.log('No viewings to cleanup');
    }
  } catch (error) {
    console.error('Error during cleanup:', error);
    // Don't throw - cleanup failure shouldn't prevent the main update
  }
};

/**
 * Verplaats een bezichtiging naar een nieuw tijdstip
 * @param {string} viewingId 
 * @param {Date|Timestamp} newStartAt 
 * @param {Date|Timestamp} newEndAt 
 */
export const rescheduleViewing = async (viewingId, newStartAt, newEndAt) => {
  try {
    // Check voor overlapping viewings
    const viewingDoc = await getDoc(doc(db, 'viewings', viewingId));
    if (!viewingDoc.exists()) {
      throw new Error('Bezichtiging niet gevonden');
    }

    const viewingData = viewingDoc.data();
    const hasOverlap = await checkViewingOverlap(
      viewingData.landlordUid, 
      newStartAt, 
      newEndAt, 
      viewingId // Exclude current viewing
    );

    if (hasOverlap) {
      throw new Error('Er is al een bezichtiging gepland op dit tijdstip');
    }

    const viewingRef = doc(db, 'viewings', viewingId);
    await updateDoc(viewingRef, {
      startAt: newStartAt instanceof Date ? Timestamp.fromDate(newStartAt) : newStartAt,
      endAt: newEndAt instanceof Date ? Timestamp.fromDate(newEndAt) : newEndAt,
      status: 'pending', // Reset naar pending voor nieuwe bevestiging
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error rescheduling viewing:', error);
    throw error;
  }
};

/**
 * Haal viewings op voor een landlord
 * @param {string} landlordUid 
 * @param {string} [status] - Optioneel filter op status
 * @returns {Promise<Viewing[]>}
 */
export const getViewingsForLandlord = async (landlordUid, status = null) => {
  try {
    let q = query(
      collection(db, 'viewings'),
      where('landlordUid', '==', landlordUid),
      orderBy('startAt', 'asc')
    );

    if (status) {
      q = query(
        collection(db, 'viewings'),
        where('landlordUid', '==', landlordUid),
        where('status', '==', status),
        orderBy('startAt', 'asc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting viewings for landlord:', error);
    throw error;
  }
};

/**
 * Haal viewings op voor een tenant
 * @param {string} tenantUid 
 * @param {string} [status] - Optioneel filter op status
 * @returns {Promise<Viewing[]>}
 */
export const getViewingsForTenant = async (tenantUid, status = null) => {
  try {
    let q = query(
      collection(db, 'viewings'),
      where('tenantUid', '==', tenantUid),
      orderBy('startAt', 'asc')
    );

    if (status) {
      q = query(
        collection(db, 'viewings'),
        where('tenantUid', '==', tenantUid),
        where('status', '==', status),
        orderBy('startAt', 'asc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting viewings for tenant:', error);
    throw error;
  }
};

/**
 * Check voor overlapping viewings
 * @param {string} landlordUid 
 * @param {Date|Timestamp} startAt 
 * @param {Date|Timestamp} endAt 
 * @param {string} [excludeViewingId] - Exclude deze viewing van de check
 * @returns {Promise<boolean>}
 */
export const checkViewingOverlap = async (landlordUid, startAt, endAt, excludeViewingId = null) => {
  try {
    const start = startAt.toDate ? startAt.toDate() : new Date(startAt);
    const end = endAt.toDate ? endAt.toDate() : new Date(endAt);

    const q = query(
      collection(db, 'viewings'),
      where('landlordUid', '==', landlordUid),
      where('status', 'in', ['pending', 'confirmed'])
    );

    const snapshot = await getDocs(q);
    
    for (const doc of snapshot.docs) {
      if (excludeViewingId && doc.id === excludeViewingId) continue;
      
      const viewing = doc.data();
      const existingStart = viewing.startAt.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
      const existingEnd = viewing.endAt.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);

      if (hasTimeOverlap(start, end, existingStart, existingEnd)) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking viewing overlap:', error);
    throw error;
  }
};

/**
 * Check of twee tijdsperiodes overlappen
 * @param {Date} start1 
 * @param {Date} end1 
 * @param {Date} start2 
 * @param {Date} end2 
 * @returns {boolean}
 */
export const hasTimeOverlap = (start1, end1, start2, end2) => {
  return start1 < end2 && start2 < end1;
};

// ===== AVAILABILITY SERVICE =====

/**
 * Stel beschikbaarheid in voor een landlord
 * @param {string} landlordUid 
 * @param {LandlordAvailability[]} availabilityRules 
 */
export const setAvailability = async (landlordUid, availabilityRules) => {
  try {
    // Verwijder bestaande regels
    const existingQuery = query(
      collection(db, 'availability'),
      where('landlordUid', '==', landlordUid)
    );
    const existingSnapshot = await getDocs(existingQuery);
    
    const deletePromises = existingSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    // Voeg nieuwe regels toe
    const addPromises = availabilityRules.map(rule => 
      addDoc(collection(db, 'availability'), {
        ...rule,
        landlordUid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    );

    await Promise.all(addPromises);
  } catch (error) {
    console.error('Error setting availability:', error);
    throw error;
  }
};

/**
 * Haal beschikbaarheid op voor een landlord
 * @param {string} landlordUid 
 * @returns {Promise<LandlordAvailability[]>}
 */
export const getAvailability = async (landlordUid) => {
  try {
    const q = query(
      collection(db, 'availability'),
      where('landlordUid', '==', landlordUid),
      orderBy('weekday', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting availability:', error);
    throw error;
  }
};

/**
 * Genereer beschikbare slots voor een landlord op een specifieke datum
 * Gebruikt alleen slots die door verhuurder zijn opgegeven (viewingSlots in property)
 * @param {string} landlordUid 
 * @param {string} listingId - Property ID om viewingSlots op te halen
 * @param {Date} date 
 * @returns {Promise<ViewingSlot[]>}
 */
export const generateAvailableSlots = async (landlordUid, listingId, date) => {
  try {
    if (!listingId) {
      return [];
    }

    // Haal property op om viewingSlots te krijgen
    const propertyRef = doc(db, 'koten', listingId);
    const propertySnap = await getDoc(propertyRef);
    
    if (!propertySnap.exists()) {
      return [];
    }

    const property = propertySnap.data();
    const viewingSlots = property.viewingSlots || [];
    
    if (viewingSlots.length === 0) {
      return []; // Geen slots opgegeven door verhuurder
    }

    const slots = [];
    const selectedDateStr = date.toISOString().split('T')[0];
    const weekday = date.getDay();
    const weekdayMap = {
      0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday',
      4: 'thursday', 5: 'friday', 6: 'saturday'
    };
    const dayOfWeek = weekdayMap[weekday];

    // Haal alle confirmed/pending viewings op voor deze landlord
    let confirmedViewings = [];
    try {
      confirmedViewings = await getViewingsForLandlord(landlordUid);
    } catch (error) {
      console.warn('Could not load viewings for overlap check:', error);
      // Continue zonder overlap check - assume alle slots beschikbaar zijn
    }
    
    const confirmedSlots = confirmedViewings
      .filter(v => v && (v.status === 'confirmed' || v.status === 'pending') && v.startAt && v.endAt)
      .map(v => {
        try {
          return {
            start: v.startAt?.toDate ? v.startAt.toDate() : new Date(v.startAt),
            end: v.endAt?.toDate ? v.endAt.toDate() : new Date(v.endAt)
          };
        } catch (err) {
          console.warn('Error parsing viewing date:', err);
          return null;
        }
      })
      .filter(v => v !== null);

    // Filter viewingSlots die matchen met de geselecteerde datum/dag
    for (const slotConfig of viewingSlots) {
      let matches = false;
      
      if (slotConfig.isRecurring) {
        // Recurring slot: check of dayOfWeek matcht
        matches = slotConfig.dayOfWeek === dayOfWeek;
      } else {
        // Specific date slot: check of datum matcht
        if (slotConfig.date) {
          const slotDate = new Date(slotConfig.date);
          const slotDateStr = slotDate.toISOString().split('T')[0];
          matches = slotDateStr === selectedDateStr;
        }
      }

      if (!matches) continue;

      // Genereer individuele slots binnen dit tijdbereik
      if (!slotConfig.startTime || !slotConfig.endTime) {
        console.warn('Slot config missing startTime or endTime:', slotConfig);
        continue;
      }
      
      const startTime = parseTime(slotConfig.startTime);
      const endTime = parseTime(slotConfig.endTime);
      const duration = slotConfig.duration || 30;
      
      if (!startTime || !endTime || startTime.hours === undefined || endTime.hours === undefined) {
        console.warn('Could not parse startTime or endTime:', slotConfig, { startTime, endTime });
        continue;
      }

      // Valideer dat eindtijd na starttijd is
      if (endTime.hours < startTime.hours || 
          (endTime.hours === startTime.hours && endTime.minutes <= startTime.minutes)) {
        console.warn('End time must be after start time:', slotConfig);
        continue;
      }

      const slotDate = new Date(date);
      slotDate.setHours(startTime.hours, startTime.minutes || 0, 0, 0);

      const endDateTime = new Date(date);
      endDateTime.setHours(endTime.hours, endTime.minutes || 0, 0, 0);

      let currentTime = new Date(slotDate);
      const maxBookings = slotConfig.maxBookings || 1;

      while (currentTime < endDateTime) {
        const slotEnd = new Date(currentTime.getTime() + duration * 60000);
        
        if (slotEnd <= endDateTime) {
          // Check hoeveel bezichtigingen al zijn gepland voor dit specifieke slot
          const bookingsForThisSlot = confirmedSlots.filter(v => {
            const viewingStart = new Date(v.start);
            const viewingEnd = new Date(v.end);
            // Check of deze viewing overlapt met dit slot
            return hasTimeOverlap(
              currentTime, slotEnd,
              viewingStart, viewingEnd
            );
          }).length;

          const isAvailable = bookingsForThisSlot < maxBookings;
          
          slots.push({
            startAt: new Date(currentTime),
            endAt: new Date(slotEnd),
            available: isAvailable,
            reason: !isAvailable ? 'Al bezet' : null,
            slotConfigId: slotConfig.id
          });
        }

        // Volgende slot
        currentTime = new Date(currentTime.getTime() + duration * 60000);
      }
    }

    // Sorteer slots op tijd
    slots.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());

    return slots;
  } catch (error) {
    console.error('Error generating available slots:', error);
    throw error;
  }
};

/**
 * Haal alle beschikbare datums op basis van viewingSlots
 * @param {string} listingId - Property ID
 * @param {number} daysAhead - Aantal dagen vooruit kijken (default 30)
 * @returns {Promise<Date[]>} - Array van datums met beschikbare slots
 */
export const getAvailableDates = async (listingId, daysAhead = 30) => {
  try {
    if (!listingId) {
      return [];
    }

    // Haal property op om viewingSlots te krijgen
    const propertyRef = doc(db, 'koten', listingId);
    const propertySnap = await getDoc(propertyRef);
    
    if (!propertySnap.exists()) {
      return [];
    }

    const property = propertySnap.data();
    const viewingSlots = property.viewingSlots || [];
    
    if (viewingSlots.length === 0) {
      return [];
    }

    const availableDates = new Set();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Genereer alle datums voor de komende dagen
    for (let i = 0; i < daysAhead; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + i);
      const weekday = checkDate.getDay();
      const weekdayMap = {
        0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday',
        4: 'thursday', 5: 'friday', 6: 'saturday'
      };
      const dayOfWeek = weekdayMap[weekday];
      const dateStr = checkDate.toISOString().split('T')[0];

      // Check of er een slot is voor deze datum
      for (const slotConfig of viewingSlots) {
        let matches = false;
        
        if (slotConfig.isRecurring) {
          // Recurring slot: check of dayOfWeek matcht
          matches = slotConfig.dayOfWeek === dayOfWeek;
        } else {
          // Specific date slot: check of datum matcht
          if (slotConfig.date) {
            const slotDate = new Date(slotConfig.date);
            const slotDateStr = slotDate.toISOString().split('T')[0];
            matches = slotDateStr === dateStr;
          }
        }

        if (matches) {
          availableDates.add(dateStr);
          break; // Alleen één match nodig om datum beschikbaar te maken
        }
      }
    }

    // Converteer terug naar Date objects
    return Array.from(availableDates)
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => a.getTime() - b.getTime());
  } catch (error) {
    console.error('Error getting available dates:', error);
    return [];
  }
};

/**
 * Parse tijd string naar hours/minutes object
 * @param {string} timeString - "09:00"
 * @returns {{hours: number, minutes: number}}
 */
const parseTime = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return null;
  }
  const parts = timeString.split(':');
  if (parts.length !== 2) {
    return null;
  }
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }
  return { hours, minutes };
};

// ===== REALTIME HOOKS =====

/**
 * Converteer viewingRequest (iOS app format) naar viewing (web app format)
 * @param {Object} request - Viewing request document
 * @returns {Object} - Viewing object
 */
const normalizeViewingRequest = (requestDoc) => {
  const data = requestDoc.data();
  const normalizedStatus = data.status === 'accepted' ? 'confirmed' : (data.status || 'pending');
  // iOS app gebruikt viewingRequests met andere veldnamen
  const normalized = {
    id: requestDoc.id,
    listingId: data.kotID || data.listingId || '', // iOS gebruikt kotID
    kotID: data.kotID || data.listingId || '', // Behoud ook kotID voor backward compat
    propertyId: data.propertyId || data.kotID || data.listingId || '',
    landlordUid: data.landlordID || data.landlordId || data.landlordUid || '',
    tenantUid: data.tenantID || data.tenantId || data.tenantUid || '',
    startAt: data.start || data.startAt, // iOS gebruikt 'start', web gebruikt 'startAt'
    endAt: data.end || data.endAt, // iOS gebruikt 'end', web gebruikt 'endAt'
    status: normalizedStatus,
    location: data.location || '',
    notes: data.notes || '',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt || data.createdAt,
    slotID: data.slotID || '', // iOS specifiek veld
    agencyId: data.agencyId || null,
    clientLinkId: data.clientLinkId || null,
    ownerId: data.ownerId || null,
    landlordName: data.landlordName || '',
    propertyTitle: data.propertyTitle || data.title || '',
    viewingsId: data.viewingsId || null,
    // Extra velden voor compatibiliteit
    _source: 'viewingRequests' // Markeer dat dit van viewingRequests komt
  };
  
  console.log('Normalizing viewingRequest:', {
    originalId: requestDoc.id,
    kotID: data.kotID,
    listingId: normalized.listingId,
    landlordID: data.landlordID,
    landlordUid: normalized.landlordUid,
    status: normalized.status
  });
  
  return normalized;
};

const toComparableTime = (value) => {
  if (!value) return 0;
  if (value?.toDate) return value.toDate().getTime();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
};

const getViewingMergeKey = (viewing) => (
  viewing?.viewingsId ||
  viewing?.linkedViewingId ||
  viewing?.id
);

const mergeViewingCollections = (...groups) => {
  const merged = new Map();

  groups.flat().forEach((viewing) => {
    if (!viewing) return;
    const key = getViewingMergeKey(viewing);
    if (!key) return;

    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, viewing);
      return;
    }

    if (existing._source === 'viewings' && viewing._source !== 'viewings') {
      merged.set(key, { ...viewing, ...existing });
      return;
    }

    if (viewing._source === 'viewings' && existing._source !== 'viewings') {
      merged.set(key, { ...existing, ...viewing });
      return;
    }

    merged.set(key, { ...existing, ...viewing });
  });

  return Array.from(merged.values()).sort((a, b) => {
    const createdDiff = toComparableTime(b.createdAt) - toComparableTime(a.createdAt);
    if (createdDiff !== 0) return createdDiff;
    return toComparableTime(a.startAt) - toComparableTime(b.startAt);
  });
};

/**
 * Realtime hook voor landlord viewings
 * Leest zowel 'viewings' (web app) als 'viewingRequests' (iOS app) collecties
 * @param {string} landlordUid 
 * @param {string} [status] 
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToLandlordViewings = (landlordUid, status, callback, errorCallback) => {
  if (!landlordUid) {
    console.warn('subscribeToLandlordViewings called without landlordUid');
    callback([]);
    return () => {};
  }

  console.log('Setting up landlord viewings subscription for:', landlordUid, 'status:', status);

  let viewingsUnsubscribe = null;
  let viewingRequestsUnsubscribe = null;
  let viewingRequestsUnsubscribe2 = null;
  let viewingRequestsUnsubscribe3 = null;
  let viewingsData = [];
  let viewingRequestsData = [];

  const mergeAndCallback = () => {
    console.log('🔄 Merging viewings:', {
      viewingsCount: viewingsData.length,
      viewingRequestsCount: viewingRequestsData.length,
      landlordUid: landlordUid,
      status: status
    });

    const allViewings = mergeViewingCollections(viewingsData, viewingRequestsData);

    console.log('📋 All viewings before status filter:', allViewings.map(v => ({
      id: v.id,
      viewingsId: v.viewingsId,
      listingId: v.listingId,
      kotID: v.kotID,
      status: v.status,
      _source: v._source
    })));

    // Filter op status als nodig
    let filtered = allViewings;
    if (status) {
      const beforeFilter = filtered.length;
      filtered = allViewings.filter(v => v.status === status);
      console.log(`🔍 Filtered by status "${status}": ${beforeFilter} → ${filtered.length}`);
    }
    
    console.log('✅ Calling callback with', filtered.length, 'total viewings');
    console.log('   - From viewings collection:', viewingsData.length);
    console.log('   - From viewingRequests collection:', viewingRequestsData.length);
    console.log('   - Breakdown by source:', {
      viewings: filtered.filter(v => v._source === 'viewings').length,
      viewingRequests: filtered.filter(v => v._source === 'viewingRequests').length
    });
    
    callback(filtered);
  };

  let viewingsUnsubscribe2 = null;

  try {
    // Subscribe to 'viewings' collection (web app) — query both landlordUid and ownerId
    let viewingsQuery;
    let viewingsQuery2;
    if (status) {
      viewingsQuery = query(
        collection(db, 'viewings'),
        where('landlordUid', '==', landlordUid),
        where('status', '==', status)
      );
      viewingsQuery2 = query(
        collection(db, 'viewings'),
        where('ownerId', '==', landlordUid),
        where('status', '==', status)
      );
    } else {
      viewingsQuery = query(
        collection(db, 'viewings'),
        where('landlordUid', '==', landlordUid)
      );
      viewingsQuery2 = query(
        collection(db, 'viewings'),
        where('ownerId', '==', landlordUid)
      );
    }

    let viewingsDataA = [];
    let viewingsDataB = [];
    const mergeViewingsQueries = () => {
      const seen = new Set();
      viewingsData = [...viewingsDataA, ...viewingsDataB].filter((v) => {
        if (seen.has(v.id)) return false;
        seen.add(v.id);
        return true;
      });
      mergeAndCallback();
    };

    viewingsUnsubscribe = onSnapshot(
      viewingsQuery,
      (snapshot) => {
        viewingsDataA = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          _source: 'viewings'
        }));
        mergeViewingsQueries();
      },
      (error) => {
        console.error('Error in viewings subscription (landlordUid):', error);
        viewingsDataA = [];
        mergeViewingsQueries();
      }
    );

    viewingsUnsubscribe2 = onSnapshot(
      viewingsQuery2,
      (snapshot) => {
        viewingsDataB = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          _source: 'viewings'
        }));
        mergeViewingsQueries();
      },
      (error) => {
        viewingsDataB = [];
        mergeViewingsQueries();
      }
    );

    // Subscribe to 'viewingRequests' collection (iOS app).
    // Uses multiple server-filtered queries on landlordID/landlordId/landlordUid
    // to stay within Firestore security rules (reading ALL docs is blocked).
    let vrDataA = [];
    let vrDataB = [];
    let vrDataC = [];

    const mergeVR = () => {
      const seen = new Set();
      viewingRequestsData = [...vrDataA, ...vrDataB, ...vrDataC].filter((v) => {
        if (seen.has(v.id)) return false;
        seen.add(v.id);
        if (status && v.status !== status) return false;
        return true;
      });
      mergeAndCallback();
    };

    const mapVR = (snap) => snap.docs.map(d => normalizeViewingRequest(d));

    const vrQ1 = status
      ? query(collection(db, 'viewingRequests'), where('landlordID', '==', landlordUid), where('status', '==', status))
      : query(collection(db, 'viewingRequests'), where('landlordID', '==', landlordUid));
    const vrQ2 = status
      ? query(collection(db, 'viewingRequests'), where('landlordId', '==', landlordUid), where('status', '==', status))
      : query(collection(db, 'viewingRequests'), where('landlordId', '==', landlordUid));
    const vrQ3 = status
      ? query(collection(db, 'viewingRequests'), where('landlordUid', '==', landlordUid), where('status', '==', status))
      : query(collection(db, 'viewingRequests'), where('landlordUid', '==', landlordUid));

    viewingRequestsUnsubscribe = onSnapshot(vrQ1, (snap) => { vrDataA = mapVR(snap); mergeVR(); }, () => { vrDataA = []; mergeVR(); });
    viewingRequestsUnsubscribe2 = onSnapshot(vrQ2, (snap) => { vrDataB = mapVR(snap); mergeVR(); }, () => { vrDataB = []; mergeVR(); });
    viewingRequestsUnsubscribe3 = onSnapshot(vrQ3, (snap) => { vrDataC = mapVR(snap); mergeVR(); }, () => { vrDataC = []; mergeVR(); });

    return () => {
      if (viewingsUnsubscribe) viewingsUnsubscribe();
      if (viewingsUnsubscribe2) viewingsUnsubscribe2();
      if (viewingRequestsUnsubscribe) viewingRequestsUnsubscribe();
      if (viewingRequestsUnsubscribe2) viewingRequestsUnsubscribe2();
      if (viewingRequestsUnsubscribe3) viewingRequestsUnsubscribe3();
    };
  } catch (error) {
    console.error('Error setting up landlord viewings subscription:', error);
    if (errorCallback) {
      errorCallback(error);
    }
    callback([]);
    return () => {
      if (viewingsUnsubscribe) viewingsUnsubscribe();
      if (viewingsUnsubscribe2) viewingsUnsubscribe2();
      if (viewingRequestsUnsubscribe) viewingRequestsUnsubscribe();
      if (viewingRequestsUnsubscribe2) viewingRequestsUnsubscribe2();
      if (viewingRequestsUnsubscribe3) viewingRequestsUnsubscribe3();
    };
  }
};

/**
 * Realtime hook voor tenant viewings
 * @param {string} tenantUid 
 * @param {string} [status] 
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToTenantViewings = (tenantUid, status, callback, errorCallback) => {
  if (!tenantUid) {
    console.warn('subscribeToTenantViewings called without tenantUid');
    callback([]);
    return () => {};
  }

  console.log('Setting up tenant viewings subscription for:', tenantUid, 'status:', status);

  let viewingsUnsubscribe = null;
  let viewingRequestsUnsubscribe = null;
  let viewingsData = [];
  let viewingRequestsData = [];
  let hasError = false;

  const mergeAndCallback = () => {
    if (hasError) {
      console.warn('⚠️ Tenant merge skipped because an error was already reported');
      return;
    }

    let merged = mergeViewingCollections(viewingsData, viewingRequestsData);
    if (status) {
      merged = merged.filter((viewing) => viewing.status === status);
    }
    callback(merged);
  };

  try {
    const viewingsQuery = query(
      collection(db, 'viewings'),
      where('tenantUid', '==', tenantUid)
    );

    viewingsUnsubscribe = onSnapshot(
      viewingsQuery,
      (snapshot) => {
        console.log('Viewings snapshot received:', snapshot.docs.length, 'docs');
        viewingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          _source: 'viewings'
        }));
        mergeAndCallback();
      },
      (error) => {
        console.error('Error in subscribeToTenantViewings:', error);
        if (errorCallback) {
          hasError = true;
          errorCallback(error);
        }
        callback([]);
      }
    );

    const viewingRequestsQuery = query(collection(db, 'viewingRequests'));
    viewingRequestsUnsubscribe = onSnapshot(
      viewingRequestsQuery,
      (snapshot) => {
        viewingRequestsData = snapshot.docs
          .filter((doc) => {
            const data = doc.data();
            const candidateTenantId = data.tenantUid || data.tenantId || data.tenantID;
            return candidateTenantId === tenantUid;
          })
          .map((doc) => normalizeViewingRequest(doc));
        mergeAndCallback();
      },
      (error) => {
        console.error('Error in tenant viewingRequests subscription:', error);
        if (errorCallback) {
          hasError = true;
          errorCallback(error);
        }
        callback([]);
      }
    );

    return () => {
      if (viewingsUnsubscribe) viewingsUnsubscribe();
      if (viewingRequestsUnsubscribe) viewingRequestsUnsubscribe();
    };
  } catch (error) {
    console.error('Error setting up tenant viewings subscription:', error);
    if (errorCallback) {
      errorCallback(error);
    }
    callback([]);
    return () => {
      if (viewingsUnsubscribe) viewingsUnsubscribe();
      if (viewingRequestsUnsubscribe) viewingRequestsUnsubscribe();
    };
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Format tijd voor display
 * @param {Date|Timestamp} time 
 * @returns {string}
 */
export const formatTime = (time) => {
  const date = time.toDate ? time.toDate() : new Date(time);
  return date.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format datum voor display
 * @param {Date|Timestamp} time 
 * @returns {string}
 */
export const formatDate = (time) => {
  const date = time.toDate ? time.toDate() : new Date(time);
  return date.toLocaleDateString('nl-NL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format status voor display
 * @param {string} status 
 * @returns {string}
 */
export const formatStatus = (status) => {
  const statusMap = {
    pending: 'In afwachting',
    confirmed: 'Bevestigd',
    declined: 'Afgewezen',
    cancelled: 'Geannuleerd',
    completed: 'Voltooid',
    no_show: 'Niet verschenen'
  };
  return statusMap[status] || status;
};

/**
 * Check of een viewing kan worden geannuleerd
 * @param {Viewing} viewing 
 * @returns {boolean}
 */
export const canCancelViewing = (viewing) => {
  const now = new Date();
  const startTime = viewing.startAt.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
  const oneHourBefore = new Date(startTime.getTime() - 60 * 60 * 1000);
  
  return now < oneHourBefore && ['pending', 'confirmed'].includes(viewing.status);
};

/**
 * Mark pending/requested viewings as read by the landlord.
 * Writes `readByLandlord: true` on both viewings and viewingRequests docs.
 * @param {Array<{id: string, _source?: string}>} viewings - viewing objects with id and _source
 */
export const markViewingsAsReadByLandlord = async (viewings) => {
  if (!viewings || viewings.length === 0) return;

  const unread = viewings.filter(
    (v) => ['pending', 'requested'].includes(v.status) && v.readByLandlord !== true
  );
  if (unread.length === 0) return;

  const batch = writeBatch(db);
  for (const v of unread) {
    const col = v._source === 'viewingRequests' ? 'viewingRequests' : 'viewings';
    batch.update(doc(db, col, v.id), { readByLandlord: true });
  }

  try {
    await batch.commit();
  } catch (error) {
    console.error('Error marking viewings as read:', error);
  }
};
